export type BusySnapshot = {
  sessionId: string | null
  busy: boolean
}

/**
 * A same-session busy→idle transition — a turn just settled, success or
 * error (PR #2429's fast-follow-up-safe transition). This is a genuinely new
 * completion event, so it always warrants a fresh drain attempt.
 */
export function isFreshSettle(prev: BusySnapshot, current: BusySnapshot): boolean {
  return prev.sessionId === current.sessionId && prev.busy && !current.busy
}

/**
 * Landing on a different, already-idle session. Covers a queue left behind
 * on a session that settled while the user was elsewhere — that in-session
 * transition already happened out of view, so without this the queue would
 * never get another chance to drain.
 */
export function isArrivalAtIdleSession(prev: BusySnapshot, current: BusySnapshot): boolean {
  return prev.sessionId !== current.sessionId && !current.busy
}

/**
 * Cursor-style message queue (#2437) — decides whether the current render is
 * a valid point to check the queue for a drain. Pure and store-agnostic so
 * the transition logic is testable without mocking the surrounding hook's
 * WebSocket/store dependencies.
 */
export function shouldDrainQueue(prev: BusySnapshot, current: BusySnapshot): boolean {
  return isFreshSettle(prev, current) || isArrivalAtIdleSession(prev, current)
}

/**
 * Drain eligibility became true while the visible session is already idle
 * with a pending queue (e.g. user turns Deep/Agent off, or the admin flag
 * flips on after a settle that was blocked). Without this, the effect only
 * watches settle/arrival transitions and the queue stays stuck forever.
 */
export function isNewlyEligibleToDrain(params: {
  wasEligible: boolean
  isEligible: boolean
  isBusy: boolean
  queueLength: number
}): boolean {
  return !params.wasEligible && params.isEligible && !params.isBusy && params.queueLength > 0
}

/** v1 queue excludes Deep Explorer — `/deep` / `/plan` must not be enqueued or auto-sent. */
export function isDeepPlanQueuePrefix(text: string): boolean {
  return /^\/(deep|plan)(\b|$)/i.test(text.trimStart())
}

/**
 * Guards an arrival-triggered attempt (`isArrivalAtIdleSession`) against
 * repeating a failed dispatch every time the user revisits an idle session —
 * without this, a permanently-undispatchable head item (e.g. a queued
 * "/deep …" that slipped in before enqueue rejection) would re-fire its send
 * attempt and error toast on every visit. A fresh settle (or newly-eligible
 * gate flip) always attempts regardless — those are new events, not revisits.
 */
export function shouldAttemptDrain(params: {
  isFreshSettle: boolean
  queueHead: string
  lastArrivalAttemptHead: string | undefined
}): boolean {
  if (params.isFreshSettle) return true
  return params.lastArrivalAttemptHead !== params.queueHead
}
